import React, { useEffect, useRef } from 'react';
import loadable from '@loadable/component';

import { bool, func, object } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';
import { useLocale } from '../../context/localeContext';
import { useIntl } from '../../util/reactIntl';

import FallbackPage from './FallbackPage';
import { ASSET_NAME, loadData } from './LandingPage.duck';
import { fetchFeaturedListings } from '../../ducks/featuredListings.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { getFeaturedListingsProps } from '../../util/data';
import SectionGreenBanner from '../PageBuilder/SectionBuilder/SectionGreenBanner/SectionGreenBanner';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

const GREEN_BANNER_SECTION_TYPE = 'customGreenBanner';

/**
 * Inject two green banner sections into the page sections array:
 * - Banner 1 after the first section (hero with search)
 * - Banner 2 after the second section (who is Fleurspace for)
 */
const injectGreenBanners = (data, banner1Text, banner2Text) => {
  if (!data?.sections || data.sections.length < 1) return data;

  const banner1 = {
    sectionType: GREEN_BANNER_SECTION_TYPE,
    sectionId: 'green-banner-1',
    headline: banner1Text.headline,
    subtext: banner1Text.subtext,
  };
  const banner2 = {
    sectionType: GREEN_BANNER_SECTION_TYPE,
    sectionId: 'green-banner-2',
    headline: banner2Text.headline,
    subtext: banner2Text.subtext,
  };

  const sections = [...data.sections];
  // Insert banner1 after the first section (index 0 → insert at index 1)
  sections.splice(1, 0, banner1);
  // Insert banner2 after what was originally the second section (index 1),
  // which is now at index 2 after banner1 was inserted (so insert at index 3)
  sections.splice(3, 0, banner2);

  return { ...data, sections };
};

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error } = props;
  const { locale } = useLocale();
  const dispatch = useDispatch();
  const intl = useIntl();

  // Track the locale that was used for the initial server/route load so we
  // don't fire a redundant fetch on first mount.
  const initialLocaleRef = useRef(locale);

  useEffect(() => {
    // Skip the very first render — loadData already ran for this locale
    // either via SSR or via the route change handler in Routes.js.
    if (locale === initialLocaleRef.current) {
      // Still update the ref in case it changed via loadData (e.g. after hydration)
      return;
    }
    // Locale changed while the user is on the landing page — re-fetch content.
    dispatch(loadData({}, '', {}));
    initialLocaleRef.current = locale;
  }, [locale, dispatch]);

  const assetKey = camelize(ASSET_NAME); // 'landingPage'

  const banner1Text = {
    headline: intl.formatMessage({ id: 'LandingPage.greenBanner1.headline' }),
    subtext: intl.formatMessage({ id: 'LandingPage.greenBanner1.subtext' }),
  };
  const banner2Text = {
    headline: intl.formatMessage({ id: 'LandingPage.greenBanner2.headline' }),
    subtext: intl.formatMessage({ id: 'LandingPage.greenBanner2.subtext' }),
  };

  const rawPageData = pageAssetsData?.[assetKey]?.data;
  const modifiedPageData = rawPageData ? injectGreenBanners(rawPageData, banner1Text, banner2Text) : rawPageData;

  return (
    <PageBuilder
      pageAssetsData={modifiedPageData}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      featuredListings={getFeaturedListingsProps(assetKey, props)}
      options={{
        sectionComponents: {
          [GREEN_BANNER_SECTION_TYPE]: { component: SectionGreenBanner },
        },
      }}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const featuredListingData = state.featuredListings || {};
  const getListingEntitiesById = listingIds => getListingsById(state, listingIds);
  return { pageAssetsData, featuredListingData, getListingEntitiesById, inProgress, error };
};

const mapDispatchToProps = dispatch => ({
  onFetchFeaturedListings: (sectionId, parentPage, listingImageConfig, allSections) =>
    dispatch(fetchFeaturedListings({ sectionId, parentPage, listingImageConfig, allSections })),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(LandingPageComponent);

export default LandingPage;
